import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, AlertCircle, Eye, EyeOff, Globe } from 'lucide-react';
import { useAuth } from '../providers/AuthProvider';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import Alert from '../components/ui/alert';

export default function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(location.state?.error || '');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = t('auth:field_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth:invalid_email');
    }
    if (!formData.password) {
      newErrors.password = t('auth:field_required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const result = await login(formData.email, formData.password, formData.rememberMe);
      if (result.requiresOnboarding) {
        navigate('/onboarding');
      } else {
        navigate('/app/dashboard');
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || t('auth:invalid_credentials'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // ✅ EMERGENT OAUTH: REMINDER - DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background bg-noise p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Globe className="w-10 h-10 text-primary" />
            <h1 className="text-3xl font-heading font-semibold tracking-tight text-text-primary">
              {t('common:app_name')}
            </h1>
          </div>
          <p className="text-text-secondary">{t('common:tagline')}</p>
        </div>

        <Card data-testid="login-card">
          <CardHeader>
            <CardTitle>{t('auth:welcome_back')}</CardTitle>
            <CardDescription>{t('auth:sign_in_to_continue')}</CardDescription>
          </CardHeader>

          <CardContent>
            {errorMessage && (
              <Alert variant="error" className="mb-6" onClose={() => setErrorMessage('')} data-testid="login-error-alert">
                {errorMessage}
              </Alert>
            )}

            <Button type="button" variant="outline" className="w-full mb-6" onClick={handleGoogleLogin} data-testid="google-login-button">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth:continue_with_google')}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface text-text-secondary">{t('auth:or_continue_with')}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-text-primary">{t('auth:email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input id="email" name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} error={!!errors.email} className="pl-10" data-testid="email-input" />
                </div>
                {errors.email && (
                  <p className="text-xs text-error flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-text-primary">{t('auth:password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={formData.password} onChange={handleChange} error={!!errors.password} className="pl-10 pr-10" data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors" data-testid="toggle-password-button">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-error flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="rememberMe" checked={formData.rememberMe} onChange={handleChange} className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20" data-testid="remember-me-checkbox" />
                  <span className="text-sm text-text-secondary">{t('auth:remember_me')}</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                  {t('auth:forgot_password')}
                </Link>
              </div>

              <Button type="submit" variant="primary" className="w-full" disabled={isLoading} data-testid="login-submit-button">
                {isLoading ? t('auth:signing_in') : t('auth:sign_in')}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex-col">
            <p className="text-sm text-text-secondary text-center">
              {t('auth:dont_have_account')}{' '}
              <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
                {t('auth:sign_up')}
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
